import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonNote,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  IonToolbar,
  NavController,
} from '@ionic/angular';
import {
  InputChangeEventDetail,
  IonInputCustomEvent,
  IonRefresherCustomEvent,
  RefresherEventDetail,
} from '@ionic/core';
import { TranslatePipe } from '@ngx-translate/core';
import { YearMonth } from '@wilson/interfaces';
import { DateTimeFormat } from '@wilson/utils';
import { format } from 'date-fns';
import { WorkingTimeBonusesComponent } from '../working-time-bonuses/working-time-bonuses.component';
import { OtherCategoriesComponent } from '../other-categories/other-categories.component';
import { WorkingTimeCardsComponent } from '../working-time-cards/working-time-cards.component';
import { WorkingTimeStatsService } from '../working-time-cards/working-time-stats.service';
import { WorkingTimeOvertimesComponent } from '../working-time-overtimes/working-time-overtimes.component';
import { WorkingTimePdfTimesheetComponent } from '../working-time-pdf-timesheet/working-time-pdf-timesheet.component';
import { Browser } from '@capacitor/browser';
import { LocalizedDateTimeFormatPipe } from '@wilson/localization';
import { select } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { MobileCompanySettingsState } from '@wilson/mobile-company-settings';
import { WorkingTimeDashboardContextService } from './working-time-dashboard-context.service';

@Component({
  selector: 'wilson-working-time-dashboard',
  templateUrl: './working-time-dashboard.component.html',
  styleUrls: ['./working-time-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [WorkingTimeDashboardContextService, WorkingTimeStatsService],
  imports: [
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonNote,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToolbar,
    TranslatePipe,
    WorkingTimeBonusesComponent,
    OtherCategoriesComponent,
    WorkingTimeCardsComponent,
    WorkingTimeOvertimesComponent,
    WorkingTimePdfTimesheetComponent,
    LocalizedDateTimeFormatPipe,
  ],
})
export class WorkingTimeDashboardComponent {
  protected readonly navController = inject(NavController);
  protected readonly dashboardContext = inject(
    WorkingTimeDashboardContextService,
  );
  private readonly translate = inject(TranslateService);

  protected readonly DateTimeFormat = DateTimeFormat;
  protected lastUpdatedDateTime = new Date();

  readonly showDashboardWorkingTimeSignal = select(
    MobileCompanySettingsState.showDashboardWorkingTime,
  );

  readonly showHomeWidgetBalanceOvertimeAccountSignal = select(
    MobileCompanySettingsState.showHomeWidgetBalanceOvertimeAccount,
  );
  readonly showDashboardOvertimeAndOtherAccountsSignal = select(
    MobileCompanySettingsState.showDashboardOvertimeAndOtherAccounts,
  );
  readonly showDashboardPayPremiumsSignal = select(
    MobileCompanySettingsState.showDashboardPayPremiums,
  );
  readonly showDashboardOtherSignal = select(
    MobileCompanySettingsState.showDashboardOther,
  );
  readonly showDashboardTimesheetSignal = select(
    MobileCompanySettingsState.showDashboardTimesheet,
  );

  protected onRefresh(
    event: IonRefresherCustomEvent<RefresherEventDetail>,
  ): void {
    setTimeout(() => {
      this.lastUpdatedDateTime = new Date();
      this.dashboardContext.triggerRefresh();
      event.detail.complete();
    }, 500);
  }

  protected onMonthInputChange(
    event: IonInputCustomEvent<InputChangeEventDetail>,
  ): void {
    const value = event.detail.value;
    if (value && typeof value === 'string') {
      this.dashboardContext.setMonthYear(value as YearMonth);
    } else {
      this.dashboardContext.setMonthYear(
        format(new Date(), DateTimeFormat.MonthFormat) as YearMonth,
      );
    }
  }

  protected async redirectToHandbook(): Promise<void> {
    await Browser.open({
      url: this.translate.instant('page.working_time.handbook_url'),
    });
  }
}
